import React, { useState, useEffect } from "react";
import * as global from '../data/global';
import { MuiMaterials } from "../@index";
import { createApiEndpoint } from '../index.js';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { createTheme } from '@mui/material/styles';
import HistorySlider from "./HistorySlider";
import DateChanger from "./DateChanger";

const pad2 = (n) => String(n).padStart(2, "0");

const localYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Local calendar date + seconds-from-midnight. Uses local clock, not the UTC 'T' field. */
const parseLocalCableRead = (dateCableRead) => {
    if (dateCableRead == null) return null;
    const d = new Date(dateCableRead);
    if (Number.isNaN(d.getTime())) return null;
    return {
        dateKey: localYmd(d),
        seconds: d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds(),
        ms: d.getTime(),
    };
};

const timeOfDayToSeconds = (timeHms) => {
    const [h, m, s] = String(timeHms || "04:00:00").split(":").map((x) => parseInt(x, 10) || 0);
    return h * 3600 + m * 60 + s;
};

const circularTimeDiff = (a, b) => {
    let diff = Math.abs(a - b);
    if (diff > 12 * 3600) diff = 24 * 3600 - diff;
    return diff;
};

/** Apply Dates ranges longer than this (inclusive calendar days) get one mark per day on Heltec sites. */
const HOURLY_HISTORY_MAX_DAYS = 4;

const inclusiveDaySpan = (fromDate, toDate) => {
    const from = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const to = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
    return Math.round((to - from) / 86400000) + 1;
};

const isWiredRead = (dcr) =>
    (dcr?.sensorHistories ?? []).some((sh) => !global.sensorIsHeltec({ sensorId: sh.sensorId }));

/**
 * One slider mark per local calendar day, anchored on:
 * - the non-Heltec (wired) scan closest to the requested time of day, when that day has one
 * - otherwise (all-Heltec site / day) the requested time of day itself
 * Every sensor then takes its reading closest to the anchor, so Heltec cables line up with the
 * wired scan and a bin is not missing just because its cables were stored under a different
 * DateCableRead. Days are never dropped.
 */
const buildHistoryMarks = (dateCableReadHistories, timeHms, formatLabel) => {
    const targetSeconds = timeOfDayToSeconds(timeHms);
    const byDate = new Map();

    for (const dcr of dateCableReadHistories ?? []) {
        const parsed = parseLocalCableRead(dcr?.dateCableRead);
        if (!parsed) continue;
        let reads = byDate.get(parsed.dateKey);
        if (!reads) {
            reads = [];
            byDate.set(parsed.dateKey, reads);
        }
        reads.push({ dcr, ...parsed, wired: isWiredRead(dcr) });
    }

    const closestTo = (reads, seconds, diffFn) => {
        let best = null;
        let bestDiff = Infinity;
        for (const r of reads) {
            const diff = diffFn(r.seconds, seconds);
            if (diff < bestDiff || (diff === bestDiff && r.ms > best.ms)) {
                best = r;
                bestDiff = diff;
            }
        }
        return best;
    };

    return Array.from(byDate.keys()).sort().map((dateKey, i) => {
        const reads = byDate.get(dateKey);
        const wiredReads = reads.filter((r) => r.wired);
        const anchor = closestTo(wiredReads.length > 0 ? wiredReads : reads, targetSeconds, circularTimeDiff);
        const anchorSeconds = wiredReads.length > 0 ? anchor.seconds : targetSeconds;
        const anchorDiff = wiredReads.length > 0 ? (a, b) => Math.abs(a - b) : circularTimeDiff;

        const sensors = new Map();
        for (const r of reads) {
            const diff = anchorDiff(r.seconds, anchorSeconds);
            for (const sh of r.dcr.sensorHistories ?? []) {
                const sid = Number(sh.sensorId);
                const key = Number.isFinite(sid) ? sid : sh.sensorId;
                if (key == null || key === "") continue;
                const prev = sensors.get(key);
                if (!prev || diff < prev.diff || (diff === prev.diff && r.ms > prev.ms)) {
                    sensors.set(key, { sh, diff, ms: r.ms });
                }
            }
        }

        return {
            label: formatLabel(anchor.dcr.dateCableRead),
            value: i + 1,
            sensorHistories: Array.from(sensors.values()).map((x) => x.sh),
            dateCableRead: anchor.dcr.dateCableRead,
        };
    });
};

const datePickerTheme = createTheme({
    components: {
        MuiButton: {
                styleOverrides: {
                    root: {
                        fontSize: "18px",
                    background: "#2d2d2d",
                    color: "rgb(244, 204, 142)",
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    sizeMedium: {
                    color: "rgb(244, 204, 142)"
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        fontSize: "18px",
                    background: "#2d2d2d",
                    color: "rgb(244, 204, 142)"
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: "18px",
                    background: "#2d2d2d",
                    color: "rgb(244, 204, 142)"
                    }
                }
            }
        }
    });

export const DateSlider = ({fromView}) => {
    const [render, setRender] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors
    const getThemeColor = (lightColor, darkColor) => {
        return global.gw.theme === 'light' ? lightColor : darkColor;
    };

    // Listen for theme changes and slider toggle (so display stays in sync with isHistorySliderOn)
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        const handleSliderToggled = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        window.addEventListener('historySliderToggled', handleSliderToggled);
        return () => {
            window.removeEventListener('themeChanged', handleThemeChange);
            window.removeEventListener('historySliderToggled', handleSliderToggled);
        };
    }, []);

    // Subscribe to marks updates
    useEffect(() => {
        if (!global.gw?.selectedSite) return;
        const handleMarksUpdate = () => {
            setRender(prev => !prev); // Toggle to force re-render
        };
        
        global.gw.selectedSite.onMarksUpdate = handleMarksUpdate;
        
        return () => {
            if (global.gw?.selectedSite) {
                global.gw.selectedSite.onMarksUpdate = null;
            }
        };
    }, []);

    const renderHistorySlider = () => {
        const marks = global.gw.selectedSite?.marks ?? [];
        let props = {
            className: "historySlider",
            marks,
            value: marks[marks.length - 1],
        };

        if (!props.value) {
            props.value = {
                value: 0
            }
        }

        // Key forces remount when marks change, clearing stale MUI Slider internal state
        const marksKey = `${marks.length}-${marks[0]?.label ?? ""}-${marks[marks.length - 1]?.label ?? ""}`;
        return (
            <HistorySlider
                key={marksKey}
                {...props}
            />
        )
    };

    const applyDates = async (fromDate, toDate, setLoading, timeOfDay) => {
        setLoading(true);
        if (!fromDate) {
            let fd = new Date();
            fd.setDate(fd.getDate() - 5);
            fromDate = fd;
        }
        if (!toDate) {
            toDate = new Date();
        }

        const { start, end } = {
            start: `${localYmd(fromDate)}T00:00:00.000Z`,
            end: `${localYmd(toDate)}T23:59:59.999Z`,
        };

        // Normalize timeOfDay to HH:mm:ss (HTML time input may return HH:mm)
        const time = timeOfDay && timeOfDay.split(":").length === 2 ? `${timeOfDay}:00` : (timeOfDay || "04:00:00");

        try {
            const response = await createApiEndpoint().fetchUserSiteHistoryWithDate("direct", start, end, time);
            const historyData = response?.data?.getUserHistoryWithDateAndTime ?? response?.data?.data?.getUserHistoryWithDateAndTime ?? response?.getUserHistoryWithDateAndTime;
            if (!historyData) {
                alert('Failed to load history for that date range. Try a shorter range if the site is large.');
                return;
            }
            if (global.gw.selectedSite) {
                const isHeltecSite = global.gw.selectedSite.heltecSensorMappings?.length > 0;
                const marks = isHeltecSite && inclusiveDaySpan(fromDate, toDate) <= HOURLY_HISTORY_MAX_DAYS
                    ? global.buildHourlyHistoryMarks(historyData.dateCableReadHistories)
                    : buildHistoryMarks(
                        historyData.dateCableReadHistories,
                        time,
                        (dcr) => global.jsonDateToFormatDate(dcr, false),
                    );
                global.gw.selectedSite.marks = marks;

                // Bin-level history for 3D fills must cover the same window as the slider (initializeHistory
                // only loads a short default range).
                global.loadSiteBinLevelHistory(start, end);

                // Show slider before re-render so isSliderOn is true when component renders
                if (global.gw.selectedSite.marks.length > 0) {
                    global.gw.selectedSite.isHistorySliderOn = true;
                    const tvcs = window.document.getElementsByClassName("cmi");
                    if (tvcs && tvcs.length > 0) {
                        const isLight = global.gw.theme === "light";
                        tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                    }
                    // Mirror toggleHistorySlider: set DOM display and dispatch so DateSlider re-renders
                    const historySlider = window.document.getElementsByClassName("dateSlider")[0];
                    if (historySlider) historySlider.style.display = "flex";
                    const dateChange = window.document.getElementsByClassName("dateChange")[0];
                    if (dateChange) dateChange.style.display = "inherit";
                    window.dispatchEvent(new CustomEvent("historySliderToggled"));
                    // Defer showing last mark label until Slider has rendered
                    setTimeout(() => {
                        const markLabels = window.document.getElementsByClassName("MuiSlider-markLabel");
                        if (markLabels && markLabels.length > 0) {
                            markLabels[markLabels.length - 1].style.display = "flex";
                        }
                    }, 100);
                }

                const lastScanLabel = (global.gw.selectedSite.lastScan || "").replace(/:\d{2}(?=\s*(?:AM|PM)?$)/i, "");
                if (!global.gw.selectedSite.marks.any(x => x.label === lastScanLabel) && global.gw.selectedSite.layoutSensors) {
                    let originalMark = {
                        label: lastScanLabel,
                        value: global.gw.selectedSite.marks.length + 1,
                        sensorHistories: [],
                        dateCableRead: global.gw.selectedSite.data?.lastScan || null,
                    };

                    global.gw.selectedSite.layoutSensors.forEach(s => {
                        originalMark.sensorHistories.push({
                            sensorId: s.sensorId,
                            sensorName: s.sensorName,
                            temperature: s.sensorValueString,
                            lastTemperature: "",
                        });
                    });

                    global.gw.selectedSite.marks.push(originalMark);
                }

                setRender(prev => !prev);
                if (global.gw.selectedSite.onMarksUpdate) {
                    global.gw.selectedSite.onMarksUpdate();
                }
            }

            let dateChanger = window.document.getElementsByClassName("dateChanger")[0];
            if (dateChanger) {
                dateChanger.style.display = "none";
            }
        } catch (e) {
            alert(e?.message || 'Failed to load history');
        } finally {
            setLoading(false);
        }
    };

    const isSliderOn = global.gw.selectedSite?.isHistorySliderOn ?? false;
    const baseStyle = fromView === "site3d" ? {
        position: "absolute",
        top: "12px",
        left: "12px",
        bottom: "12px",
        margin: "0",
        textAlign: "center",
        justifyContent: "center",
        alignItems: "flex-start",
        flexDirection: "column",
        zIndex: "10",
        display: isSliderOn ? "flex" : "none",
        padding: "18px 0px 18px 0px",
        width: "185px",
        background: "#1212126e",
        borderColor: "#f4cc8e",
        borderStyle: "solid",
        borderWidth: "1px",
        height: "auto",
    } : {
        position: " absolute",
        top: "49px",
        left: "2px",
        bottom: "0px",
        margin: "auto",
        textAlign: "center",
        justifyContent: "center",
        alignItems: "flex-start",
        flexDirection: "column",
        zIndex: "10",
        display: isSliderOn ? "flex" : "none",
        padding: "18px 0px 18px 0px",
        width: "185px",
        background: "#1212126e",
        borderColor: "#f4cc8e",
        borderStyle: "solid",
        borderWidth: "1px",
        height: "calc(100vh - 129px)"
    };

    return (
        <>
            <div
                className="dateSlider"
                style={baseStyle}
            >
                {renderHistorySlider()}
                <div
                    className="dateChange"
                    style={{
                        position: "absolute",
                        top: "-1px",
                        right: "-72px",
                        margin: "auto",
                        textAlign: "center",
                        display: isSliderOn ? "flex" : "none",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: "10",
                    }}
                >
                <MuiMaterials.Button
                        key={`dateslider-calendar-${global.gw.theme}-${forceUpdate}`}
                    sx={{
                        fontSize: "10px",
                        color: getThemeColor("rgb(51, 51, 51)", "#f4cc8e"),
                            background: getThemeColor("rgba(255, 255, 255, 0.9)", "#1212126e"),
                            left: "-185px",
                            right: "0px",
                            top: "-43px",
                            borderColor: getThemeColor("#cccccc", "#f4cc8e"),
                            borderStyle: "solid",
                            borderWidth: "1px",
                            position: "static",
                        '&:hover': {
                                background: getThemeColor("rgba(255, 255, 255, 1)", "#1212126e"),
                                borderColor: getThemeColor("#999999", "#f4cc8e"),
                        },
                        '& .MuiSvgIcon-root': {
                            color: getThemeColor("rgb(51, 51, 51)", "#f4cc8e"),
                        },
                    }}
                    onClick={() => {
                        let dateChanger = window.document.getElementsByClassName("dateChanger")[0];
                        if (dateChanger) {
                            if (dateChanger.style.display === "flex") {
                                dateChanger.style.display = "none";
                            }
                            else {
                                dateChanger.style.display = "flex";
                            }
                        }
                            //let dateSlider = window.document.getElementsByClassName("dateSlider")[0];
                            //if (dateSlider) {
                            //    if (dateSlider.style.display === "flex") {
                            //        dateSlider.style.display = "none";
                            //    }
                            //    else {
                            //        dateSlider.style.display = "flex";
                            //    }
                            //}
                    }}
                >
                        <CalendarMonthIcon />
                </MuiMaterials.Button>
                </div>
                <DateChanger applyDates={applyDates}/>
            </div>
        </>
    );
}

export default DateSlider;

